import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { getCompanyInfo } from '../../features/utility/utilitySlice'

import './Dashboard.css'
import DashboardBackgroundImage from '@/assets/images/awardsapp-bg.webp'
import CButton from '../../components/UI/CButton'
import Icon from '../../components/UI/Icon'
import CDialog from '../../components/UI/CDialog'
import ChangePasswordForm from './components/ChangePasswordForm'
import CSkeleton from '../../components/UI/CSkeleton'

const Dashboard = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const { user } = useSelector((state) => state.auth)
    const { companyInfo, loading } = useSelector((state) => state.utility)

    const isLoadingCompanyInfo = loading.companyInfo

    const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false)

    const displayCompanyInfo = useMemo(() => ({
        organisationname: companyInfo?.organisation?.organisationname || 'Not available',
        registrationnumber: companyInfo?.organisation?.registrationnumber || 'Not available',
        vatlicencenumber: companyInfo?.organisation?.vatlicencenumber || 'Not available',
        line1: companyInfo?.address?.line1 || 'Not available',
        suburb: companyInfo?.address?.suburb || '',
        postcode: companyInfo?.address?.postcode || '',
        city: companyInfo?.address?.city || '',
    }), [companyInfo])

    useEffect(() => {
        dispatch(getCompanyInfo())
    }, [dispatch])

    const handleChangePasswordClick = () => {
        setChangePasswordDialogOpen(true)
    };

    const handleAddUserClick = () => {
        navigate('/users', { state: { openDialog: 'addUser' } })
    };

    const handleImportUsersClick = () => {
        navigate('/users', { state: { openDialog: 'uploadUsers' } })
    };

    const handlePurchaseFundsClick = () => {
        navigate('/account', { state: { openDialog: 'purchaseFunds' } })
    };

    const handleChangePasswordDialogClose = () => {
        setChangePasswordDialogOpen(false)
    }

    const handleChangePasswordSuccess = () => {
        setChangePasswordDialogOpen(false)
    }

    return (
        <div className="dashboard">
            <div className="column">
                <div className="user-account container-style">
                    <div className="sub-container">
                        <div className="container-title">
                            <div className="icon-wrapper">
                                <Icon name="user-profile-setting" />
                            </div>
                            <span className="header-4">Your profile</span>
                        </div>

                        <span className="header-3 user-name">{user?.firstnames} {user?.surname}</span>
                        <span className="text-4 user-email">{user?.securityemail}</span>
                        <span className="text-4 user-cell">{user?.securitycell}</span>
                    </div>

                    <div className="divider"></div>
                    <div className="bottom-container">
                        <div className="container-left">
                            <span className="header-4">Change password</span>
                            <span className="text-5">Need to update your password? Start the reset process here.</span>
                        </div>

                        <CButton
                            className="change-password-button"
                            variant="contained"
                            size="large"
                            onClick={handleChangePasswordClick}
                            colorTheme="primary"
                            label="Change password"
                        />
                    </div>
                </div>

                <div className="company-information container-style">
                    {isLoadingCompanyInfo ? (
                        <CSkeleton lines={3} variant="text" className="company-info-skeleton" />
                    ) : (
                        <>
                            <div className="sub-container">
                                <div className="container-title">
                                    <div className="icon-wrapper">
                                        <Icon name="user-profile-setting" />
                                    </div>
                                    <span className="header-4">Company Information</span>
                                </div>

                                <div className="company-info-headers">
                                    <span className="header-3 company-name">{displayCompanyInfo.organisationname}</span>
                                    <div className="company-info-headers-grid">
                                        <span className="text-4 company-registration">Reg. No.</span>
                                        <span className="text-4 company-registration">{displayCompanyInfo.registrationnumber}</span>
                                        <span className="text-4 company-vatlicencenumber">VAT No.</span>
                                        <span className="text-4 company-vatlicencenumber">{displayCompanyInfo.vatlicencenumber}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="divider"></div>
                            <div className="bottom-container">
                                <span className="header-4 address">Address</span>
                                <span className="text-4">{displayCompanyInfo.line1}</span>
                                {displayCompanyInfo.suburb && <span className="text-4">{displayCompanyInfo.suburb}</span>}
                                {displayCompanyInfo.postcode && <span className="text-4">{displayCompanyInfo.postcode}</span>}
                                {displayCompanyInfo.city && <span className="text-4">{displayCompanyInfo.city}</span>}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="column">
                <div className="image-thing container-style">
                    <img src={DashboardBackgroundImage} alt="dashboard-image" />
                    <div className="image-overlay-text">
                        <div className="headers">
                            <span className="header-1">Welcome.</span>
                            <span className="header-2">Add your team. Load funds.</span>
                            <span className="header-2">Start rewarding in minutes.</span>
                        </div>
                        <span className="text-3">Everything you need lives here: onboard users, purchase funds or load wallets for your recipients and give them their choice of rewards. You can even track spend activity as you go.</span>
                        <span className="text-4">Simple to start. Built to scale.</span>
                    </div>
                </div>

                <div className="quick-links-wrapper">
                    <h2 className="quick-links-title header-3">Quick links</h2>
                    <div className="quick-links">
                        <div className="quick-link-card container-style" onClick={handleAddUserClick} tabIndex={0} role="button" onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleAddUserClick()}>
                            <div className="quick-link-header">
                                <div className="icon-wrapper">
                                    <Icon name="multiple-actions-add" />
                                </div>
                            </div>
                            <div className="container-content">
                                <div className="quick-link-titles">
                                    <span className="header-4">Invite your team</span>
                                </div>
                                <p className="text-5 quick-link-description">
                                    Add a team member and allocate rewards when you're ready.
                                </p>
                            </div>
                            <div className="card-nav">
                                <span className="header-5">Add a user</span>
                                <Icon name="arrow-right" />
                            </div>
                        </div>

                        <div className="quick-link-card container-style" onClick={handleImportUsersClick} tabIndex={0} role="button" onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleImportUsersClick()}>
                            <div className="quick-link-header">
                                <div className="icon-wrapper">
                                    <Icon name="multiple-actions-add" />
                                </div>
                            </div>
                            <div className="container-content">
                                <div className="quick-link-titles">
                                    <span className="header-4">Upload multiple users</span>
                                </div>
                                <p className="text-5 quick-link-description">
                                    Import a spreadsheet to quickly onboard your team.
                                </p>
                            </div>
                            <div className="card-nav">
                                <span className="header-5">Import users</span>
                                <Icon name="arrow-right" />
                            </div>
                        </div>

                        <div className="quick-link-card container-style" onClick={handlePurchaseFundsClick} tabIndex={0} role="button" onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handlePurchaseFundsClick()}>
                            <div className="quick-link-header">
                                <div className="icon-wrapper">
                                    <Icon name="credit-card-sync" />
                                </div>
                            </div>
                            <div className="container-content">
                                <div className="quick-link-titles">
                                    <span className="header-4">Purchase funds</span>
                                </div>
                                <p className="text-5 quick-link-description">
                                    Increase your available points to continue rewarding.
                                </p>
                            </div>
                            <div className="card-nav">
                                <span className="header-5">Purchase funds</span>
                                <Icon name="arrow-right" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>




            {/* Change Password Dialog */}
            <CDialog
                open={changePasswordDialogOpen}
                onClose={handleChangePasswordDialogClose}
                title="Change Password"
                titleIcon={"lock"}
                maxWidth="sm"
                fullWidth
            >
                <ChangePasswordForm
                    onClose={handleChangePasswordDialogClose}
                    onSuccess={handleChangePasswordSuccess}
                />
            </CDialog>
        </div >
    )
}

export default Dashboard
